import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, unref as _unref } from "vue";
const _hoisted_1 = {
  class: "allocation-table space-y-4"
};
const _hoisted_2 = {
  key: 0,
  class: "flex gap-4 items-end"
};
const _hoisted_3 = {
  class: "allocation-table__wrapper"
};
const _hoisted_4 = {
  class: "allocation-table__header-table"
};
const _hoisted_5 = {
  key: 0,
  class: "w-5 bg-transparent border-0 border-b border-gray-300 text-gray-700 font-medium text-sm p-4"
};
const _hoisted_6 = {
  key: 1,
  class: "font-medium text-sm w-12 bg-transparent border-0 border-b border-gray-300 text-gray-700 p-4",
  "aria-label": "Actions"
};
const _hoisted_7 = {
  class: "allocation-table__scroll-container"
};
const _hoisted_8 = {
  class: "allocation-table__body-table"
};
const _hoisted_9 = ["data-cy"];
const _hoisted_10 = {
  key: 0,
  class: "text-center w-10 text-gray-900 p-4 bg-transparent border-0 text-sm overflow-wrap-break-word"
};
const _hoisted_11 = {
  class: "text-gray-900 p-4 bg-transparent border-0 text-sm overflow-wrap-break-word"
};
const _hoisted_12 = {
  class: "font-medium text-gray-900"
};
const _hoisted_13 = {
  key: 0,
  class: "text-gray-400"
};
const _hoisted_14 = {
  key: 1,
  class: "text-center w-12"
};
const _hoisted_15 = {
  key: 0
};
const _hoisted_16 = ["colspan"];
const _hoisted_17 = {
  key: 0,
  class: "allocation-table__footer-table"
};
const _hoisted_18 = {
  class: "font-medium bg-transparent border-0 border-b-2 border-gray-300"
};
const _hoisted_19 = {
  key: 0,
  class: "w-5 bg-transparent border-0 p-4"
};
const _hoisted_20 = {
  key: 1,
  class: "w-12 bg-transparent border-0 p-4"
};
const _hoisted_21 = {
  class: "bg-transparent border-0 border-t border-gray-300 border-b border-gray-300"
};
const _hoisted_22 = {
  key: 0,
  class: "w-5 bg-transparent border-0 p-4"
};
const _hoisted_23 = {
  key: 0,
  class: "font-semibold text-gray-900"
};
const _hoisted_24 = {
  key: 1,
  class: "w-12"
};
const _hoisted_25 = {
  key: 1,
  class: "mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm",
  role: "status"
};
import { computed } from 'vue';
import { formatCurrency, DEFAULT_STEP, round2, isAmountEqual, PERCENTAGE_SCALE } from '@/services/utils';
import { useAllocationCalculations } from '@/composables/useAllocationCalculations';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';

// Re-declare for use in setup scope (needed for template access)

// Constants must be outside <script setup> to be available for prop validator
const ALLOCATION_MODES = {
  PERCENTAGE: 0,
  WORKED_HOURS: 1,
  WORKED_AMOUNT: 2
};
export default {
  __name: 'AllocationTable',
  props: {
    mode: {
      type: Number,
      required: true,
      validator: val => Object.values(ALLOCATION_MODES).includes(val)
    },
    allocations: {
      type: Array,
      required: true
    },
    chargeAmount: {
      type: Number,
      default: 0
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    canAddUsers: {
      type: Boolean,
      default: false
    },
    availableUsers: {
      type: Array,
      default: () => []
    },
    validationMessage: {
      type: String,
      default: ''
    }
  },
  emits: ['update:allocations', 'remove:allocation', 'add:allocation', 'split:evenly', 'reset:defaults', 'field:update', 'update:invoiceUser'],
  setup(__props, {
    emit: __emit
  }) {
    const ALLOCATION_MODES_SETUP = ALLOCATION_MODES;
    const props = __props;
    const emit = __emit;

    // Use centralized allocation calculations composable
    const {
      roundToDecimals,
      calculateTotals,
      roundPercentageForStorage,
      PERCENTAGE_SCALE: COMPOSABLE_SCALE,
      DISPLAY_PRECISION
    } = useAllocationCalculations();

    // Ensure we use the same scale
    const PERCENTAGE_SCALE_LOCAL = COMPOSABLE_SCALE;

    // Dynamic column definitions based on mode
    const displayColumns = computed(() => {
      const columns = [];
      switch (props.mode) {
        case ALLOCATION_MODES_SETUP.PERCENTAGE:
          return [{
            key: 'percentage',
            label: 'Share (%)',
            align: 'left',
            editable: true,
            step: DEFAULT_STEP,
            max: PERCENTAGE_SCALE,
            suffix: '%',
            inputClass: 'w-full',
            format: val => `${val.toFixed(2)}%`
          }, {
            key: 'amount',
            label: 'Share Amount',
            align: 'left',
            editable: true,
            step: DEFAULT_STEP,
            inputClass: 'w-full',
            prefix: '$',
            format: val => formatCurrency(val)
          }];
        case ALLOCATION_MODES_SETUP.WORKED_HOURS:
          return [{
            key: 'workedHours',
            label: 'Worked Hours',
            align: 'right',
            editable: false,
            format: val => val.toFixed(2)
          }, {
            key: 'percentage',
            label: 'Share (%)',
            align: 'right',
            editable: false,
            format: val => `${val.toFixed(2)}%`
          }, {
            key: 'amount',
            label: 'Share Amount',
            align: 'right',
            editable: false,
            format: val => formatCurrency(val)
          }];
        case ALLOCATION_MODES_SETUP.WORKED_AMOUNT:
          return [{
            key: 'workedAmount',
            label: 'Worked Amount',
            align: 'right',
            editable: false,
            format: val => formatCurrency(val)
          }, {
            key: 'percentage',
            label: 'Share (%)',
            align: 'right',
            editable: false,
            format: val => `${val.toFixed(2)}%`
          }, {
            key: 'amount',
            label: 'Share Amount',
            align: 'right',
            editable: false,
            format: val => formatCurrency(val)
          }];
        default:
          return [];
      }
    });

    // Computed totals for footer
    const totals = computed(() => {
      return calculateTotals(props.allocations);
    });

    // Validation for percentage mode
    const isAllocationValid = computed(() => {
      if (props.allocations.length === 0) return true;
      return isAmountEqual(totals.value.amount, props.chargeAmount);
    });

    // Empty state message based on mode
    const emptyStateMessage = computed(() => {
      if (props.mode === ALLOCATION_MODES_SETUP.PERCENTAGE) {
        return 'Add users to create allocations';
      } else if (props.mode === ALLOCATION_MODES_SETUP.WORKED_HOURS) {
        return 'No worked hours data available. Make sure time entries are logged for this fixed fee.';
      } else if (props.mode === ALLOCATION_MODES_SETUP.WORKED_AMOUNT) {
        return 'No worked amount data available. Make sure time entries are logged for this fixed fee.';
      }
      return 'No allocations available';
    });

    // Format cell values based on column type
    const formatCellValue = (column, allocation) => {
      const value = allocation[column.key] || 0;
      return column.format ? column.format(value) : value;
    };

    // Format percentage value for input display (2 decimals)
    const formatPercentageForInput = value => {
      return roundToDecimals(Number(value) || 0, DISPLAY_PRECISION);
    };

    // Event handlers
    const handleFieldUpdate = (accountId, field, value) => {
      const updatedAllocations = [...props.allocations];
      const allocation = updatedAllocations.find(a => a.accountId === accountId);
      if (!allocation) return;
      if (field === 'percentage') {
        // Handle percentage change like onPercentageChanged in RevenueAllocationTab.vue
        if (!props.chargeAmount || props.chargeAmount <= 0) return;
        allocation.percentage = roundPercentageForStorage(Number(value) || 0);
        if (allocation.percentage < 0) allocation.percentage = 0;
        if (allocation.percentage > PERCENTAGE_SCALE_LOCAL) allocation.percentage = PERCENTAGE_SCALE_LOCAL;

        // Update all allocations' amounts based on their percentages (exact same logic as old component)
        updatedAllocations.forEach(alloc => {
          alloc.amount = Math.round((alloc.percentage || 0) * props.chargeAmount) / PERCENTAGE_SCALE_LOCAL;
        });
      } else if (field === 'amount') {
        // Other rows are never rebalanced: the user owns every amount, and the
        // totals validation blocks saving until they add up to the fee.
        allocation.amount = Math.max(0, Number(value) || 0);
        if (props.mode === ALLOCATION_MODES_SETUP.PERCENTAGE && props.chargeAmount > 0) {
          allocation.percentage = roundPercentageForStorage(allocation.amount / props.chargeAmount * PERCENTAGE_SCALE_LOCAL);
        }
      }
      emit('update:allocations', updatedAllocations);
    };
    const handleRemoveAllocation = accountId => {
      emit('remove:allocation', accountId);
    };
    const handleInvoiceUserChange = (allocation, isInvoiceUser) => {
      // Directly mutate the allocations array - this is the key difference
      // We need to update the allocation's main property directly
      const updatedAllocation = props.allocations.find(a => a.accountId === allocation.accountId);
      if (updatedAllocation) {
        // Set this allocation as the invoice user
        updatedAllocation.main = isInvoiceUser;

        // Ensure only one allocation is marked as main
        props.allocations.forEach(a => {
          if (a.accountId !== allocation.accountId) {
            a.main = false;
          }
        });

        // Emit the updated allocations array (same reference, but with mutated values)
        emit('update:allocations', [...props.allocations]);
        if (isInvoiceUser) {
          emit('update:invoiceUser', updatedAllocation);
        } else {
          emit('update:invoiceUser', null);
        }
      }
    };
    const handleAddUser = userId => {
      if (userId) {
        emit('add:allocation', userId);
      }
    };
    const handleSplitEvenly = () => {
      emit('split:evenly');
    };
    const handleResetToDefaults = () => {
      emit('reset:defaults');
    };
    return (_ctx, _cache) => {
      const _component_v_icon = _resolveComponent("v-icon");
      const _component_v_list_item_title = _resolveComponent("v-list-item-title");
      const _component_v_list_item = _resolveComponent("v-list-item");
      const _component_v_list = _resolveComponent("v-list");
      const _component_v_menu = _resolveComponent("v-menu");
      return _openBlock(), _createElementBlock("div", _hoisted_1, [__props.canAddUsers && !__props.isReadOnly ? (_openBlock(), _createElementBlock("div", _hoisted_2, [_createVNode(_component_v_menu, {
        "offset-y": ""
      }, {
        activator: _withCtx(({
          props
        }) => [_createVNode(LlSecondaryButton, _mergeProps({
          variant: "flat",
          class: "!bg-[rgb(var(--v-theme-surface))]",
          size: "small"
        }, props, {
          "data-cy": "allocation-add-user"
        }), {
          default: _withCtx(() => [_createVNode(_component_v_icon, {
            size: "small",
            class: "mr-1"
          }, {
            default: _withCtx(() => [...(_cache[0] || (_cache[0] = [_createTextVNode("mdi-plus", -1)]))]),
            _: 1
          }), _cache[1] || (_cache[1] = _createTextVNode(" Add User ", -1))]),
          _: 1
        }, 16)]),
        default: _withCtx(() => [_createVNode(_component_v_list, null, {
          default: _withCtx(() => [(_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.availableUsers, user => {
            return _openBlock(), _createBlock(_component_v_list_item, {
              key: user.id,
              onClick: $event => handleAddUser(user.id),
              "data-cy": "allocation-add-user-option",
              class: "max-h-80"
            }, {
              default: _withCtx(() => [_createVNode(_component_v_list_item_title, null, {
                default: _withCtx(() => [_createTextVNode(_toDisplayString(user.name), 1)]),
                _: 2
              }, 1024)]),
              _: 2
            }, 1032, ["onClick"]);
          }), 128)), __props.availableUsers.length === 0 ? (_openBlock(), _createBlock(_component_v_list_item, {
            key: 0,
            disabled: ""
          }, {
            default: _withCtx(() => [_createVNode(_component_v_list_item_title, {
              class: "text-gray-500"
            }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [_createTextVNode("No users available", -1)]))]),
              _: 1
            })]),
            _: 1
          })) : _createCommentVNode("", true)]),
          _: 1
        })]),
        _: 1
      })])) : _createCommentVNode("", true), _createElementVNode("div", _hoisted_3, [_createElementVNode("table", _hoisted_4, [_createElementVNode("thead", null, [_createElementVNode("tr", null, [!__props.isReadOnly ? (_openBlock(), _createElementBlock("th", _hoisted_5)) : _createCommentVNode("", true), _cache[3] || (_cache[3] = _createElementVNode("th", {
        class: "text-left font-medium text-sm min-w-48 bg-transparent border-0 border-b border-gray-300 text-gray-700 p-4"
      }, " User ", -1)), (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayColumns.value, column => {
        return _openBlock(), _createElementBlock("th", {
          key: column.key,
          class: _normalizeClass([column.align === 'right' ? 'text-right' : 'text-left', 'font-medium text-sm bg-transparent border-0 border-b border-gray-300 text-gray-700 p-4'])
        }, _toDisplayString(column.label), 3);
      }), 128)), !__props.isReadOnly ? (_openBlock(), _createElementBlock("th", _hoisted_6)) : _createCommentVNode("", true)])])]), _createElementVNode("div", _hoisted_7, [_createElementVNode("table", _hoisted_8, [_createElementVNode("tbody", null, [(_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.allocations, (allocation, index) => {
        return _openBlock(), _createElementBlock("tr", {
          key: `${allocation.accountId}-${allocation.main}`,
          "data-cy": `allocation-row-${index}`,
          class: "border-0 border-b border-gray-300 hover:bg-gray-50"
        }, [!__props.isReadOnly ? (_openBlock(), _createElementBlock("td", _hoisted_10, [_createVNode(LlCheckbox, {
          "model-value": allocation.main,
          "onUpdate:modelValue": val => handleInvoiceUserChange(allocation, val),
          disabled: __props.isReadOnly,
          "aria-label": `Make ${allocation.accountName} the invoice user`,
          "data-cy": `allocation-invoice-user-${index}`
        }, null, 8, ["model-value", "onUpdate:modelValue", "disabled", "aria-label", "data-cy"])])) : _createCommentVNode("", true), _createElementVNode("td", _hoisted_11, [_createElementVNode("span", _hoisted_12, _toDisplayString(allocation.accountName), 1)]), (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayColumns.value, column => {
          return _openBlock(), _createElementBlock("td", {
            key: `${allocation.accountId}-${column.key}`,
            class: _normalizeClass([column.align === 'right' ? 'text-right' : 'text-left', 'text-gray-900 p-4 bg-transparent border-0 text-sm overflow-wrap-break-word'])
          }, [__props.isReadOnly || !column.editable ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(formatCellValue(column, allocation)), 1)) : (_openBlock(), _createBlock(LlTextField, {
            key: 1,
            "model-value": column.key === 'percentage' ? formatPercentageForInput(allocation[column.key]) : allocation[column.key],
            type: "number",
            step: column.step || _unref(DEFAULT_STEP),
            min: 0,
            max: column.max,
            suffix: column.suffix,
            prefix: column?.prefix,
            variant: "outlined",
            density: "compact",
            "hide-details": "",
            class: _normalizeClass(column.inputClass),
            "data-cy": `allocation-${column.key}-${index}`,
            "onUpdate:modelValue": val => handleFieldUpdate(allocation.accountId, column.key, val)
          }, null, 8, ["model-value", "step", "max", "suffix", "prefix", "class", "data-cy", "onUpdate:modelValue"]))], 2);
        }), 128)), !__props.isReadOnly ? (_openBlock(), _createElementBlock("td", _hoisted_14, [_createVNode(LlSecondaryButton, {
          icon: "mdi-trash-can-outline",
          size: "small",
          "data-cy": `allocation-delete-${index}`,
          onClick: $event => handleRemoveAllocation(allocation.accountId),
          "aria-label": "Remove allocation",
          variant: "text"
        }, null, 8, ["data-cy", "onClick"])])) : _createCommentVNode("", true)], 8, _hoisted_9);
      }), 128)), __props.allocations.length === 0 ? (_openBlock(), _createElementBlock("tr", _hoisted_15, [_createElementVNode("td", {
        colspan: displayColumns.value.length + 4,
        class: "text-center text-gray-500 py-8"
      }, _toDisplayString(emptyStateMessage.value), 9, _hoisted_16)])) : _createCommentVNode("", true)])])]), __props.allocations.length > 0 ? (_openBlock(), _createElementBlock("table", _hoisted_17, [_createElementVNode("tfoot", null, [_createElementVNode("tr", _hoisted_18, [!__props.isReadOnly ? (_openBlock(), _createElementBlock("td", _hoisted_19)) : _createCommentVNode("", true), _cache[4] || (_cache[4] = _createElementVNode("td", {
        class: "font-semibold text-gray-900 bg-transparent border-0 p-4 min-w-48"
      }, " Total ", -1)), (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayColumns.value, column => {
        return _openBlock(), _createElementBlock("td", {
          key: `total-${column.key}`,
          class: _normalizeClass([column.align === 'right' ? 'text-right' : 'text-left', 'font-medium bg-transparent border-0 p-4'])
        }, _toDisplayString(formatCellValue(column, totals.value)), 3);
      }), 128)), !__props.isReadOnly ? (_openBlock(), _createElementBlock("td", _hoisted_20)) : _createCommentVNode("", true)]), _createElementVNode("tr", _hoisted_21, [!__props.isReadOnly ? (_openBlock(), _createElementBlock("td", _hoisted_22)) : _createCommentVNode("", true), _cache[5] || (_cache[5] = _createElementVNode("td", {
        class: "font-medium text-gray-900 bg-transparent border-0 p-4"
      }, " Fixed Fee Amount ", -1)), (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayColumns.value, column => {
        return _openBlock(), _createElementBlock("td", {
          key: `fixedfee-${column.key}`,
          class: _normalizeClass([column.align === 'right' ? 'text-right' : 'text-left', 'bg-transparent border-0 p-4'])
        }, [column.key === 'amount' ? (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(_unref(formatCurrency)(__props.chargeAmount)), 1)) : _createCommentVNode("", true)], 2);
      }), 128)), !__props.isReadOnly ? (_openBlock(), _createElementBlock("td", _hoisted_24)) : _createCommentVNode("", true)])])])) : _createCommentVNode("", true)]), __props.mode === _unref(ALLOCATION_MODES_SETUP).PERCENTAGE && __props.validationMessage ? (_openBlock(), _createElementBlock("div", _hoisted_25, [_createVNode(_component_v_icon, {
        size: "small",
        class: "mr-2"
      }, {
        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [_createTextVNode("mdi-information", -1)]))]),
        _: 1
      }), _createTextVNode(" " + _toDisplayString(__props.validationMessage), 1)])) : _createCommentVNode("", true)]);
    };
  }
};